
import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  BackNavigable,
  BackNavigationService,
  showRequestErrors,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import { Store } from '@softline/core';
import { ModalStore, SOFTLINE_FEATURE_MODAL } from '@softline/ui-core';
import { Subscription } from 'rxjs';
import { DeactivateComponent } from '../dialogs/deactivate/deactivate.component';
import { Deactivateable } from '../guards/on-deactivate.guard';
import {
  SOFTLINE_FEATURE_MDE_MATERIALLIEFERSCHEIN_SCAN,
  SOFTLINE_FEATURE_MDE_MATERIALLIEFERSCHEIN_SCAN_BEWE,
} from '../materiallieferschein-scan.shared';
import { MateriallieferscheinScanBeweStore } from '../stores/materiallieferschein-scan-bewe.store';
import { MateriallieferscheinScanStore } from '../stores/materiallieferschein-scan.store';

@Component({
  selector: 'soft-materiallieferschein-scan',
  imports: [RouterModule],
  templateUrl: './materiallieferschein-scan.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./materiallieferschein-scan.component.css'],
})
export class MateriallieferscheinScanComponent
  implements OnInit, OnDestroy, BackNavigable, Deactivateable
{
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly backNavigationService = inject(BackNavigationService);

  private subscription?: Subscription;
  private subscription2?: Subscription;



  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      'Material-/Lieferschein'
    );
    this.subscription = this.activatedRoute.data.subscribe(
      async ({ materiallieferscheinScan }) => {
        if (materiallieferscheinScan?.kopf) {
          this.store.commit(
            SOFTLINE_FEATURE_MDE_MATERIALLIEFERSCHEIN_SCAN,
            MateriallieferscheinScanStore.mutations.setKopf,
            materiallieferscheinScan.kopf
          );
        }
        if (!materiallieferscheinScan?.kopf) {
          await this.router.navigate(['/materiallieferschein-scan/einlesen']);
        }
      }
    );
    this.subscription2 = this.store
      .observe(
        SOFTLINE_FEATURE_MDE_MATERIALLIEFERSCHEIN_SCAN,
        MateriallieferscheinScanStore.getters.kopf
      )
      .subscribe(async (kopf) => {
        if (!kopf) return;
        await this.store.dispatch(
          SOFTLINE_FEATURE_MDE_MATERIALLIEFERSCHEIN_SCAN_BEWE,
          MateriallieferscheinScanBeweStore.actions.loadMany,
          { clear: true, pathParams: { idkopflf: kopf.id } }
        );
      });
  }

  ngOnDestroy(): void {
    this.backNavigationService.set(undefined);
    this.subscription?.unsubscribe();
    this.subscription2?.unsubscribe();
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
  }

  async canDeactivate(): Promise<boolean> {
    const kopf = this.store.get(
      SOFTLINE_FEATURE_MDE_MATERIALLIEFERSCHEIN_SCAN,
      MateriallieferscheinScanStore.getters.kopf
    );
    if (!kopf || !kopf?.id) return true;
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<'LOESCHEN' | 'ABSCHLIESSEN', object>(),
      {
        component: DeactivateComponent,
        dismiss: true,
      }
    );
    if (result === 'DISMISSED') return false;
    if (result === 'LOESCHEN') {
      try {
        await this.store.dispatch(
          SOFTLINE_FEATURE_MDE_MATERIALLIEFERSCHEIN_SCAN,
          MateriallieferscheinScanStore.actions.removeBeleg
        );
      } catch (e) {
        showRequestErrors(this.store, e);
      }
    } else if (result === 'ABSCHLIESSEN') {
      try {
        await this.store.dispatch(
          SOFTLINE_FEATURE_MDE_MATERIALLIEFERSCHEIN_SCAN,
          MateriallieferscheinScanStore.actions.abschluss
        );
      } catch (e) {
        showRequestErrors(this.store, e);
      }
    }
    return true;
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/']);
  }
}
